import type { AgendaWindow } from "./shellTypes";

export function buildTodayWindow(timezone: string, now = new Date()): AgendaWindow {
  const parts = getTimeZoneParts(now, timezone);
  const start = new Date(zonedDateTimeLocalToISOString(formatLocalDate(parts, "00:00"), timezone));
  const nextDay = getUTCDateParts(new Date(Date.UTC(parts.year, parts.month - 1, parts.day + 1)));
  const end = new Date(new Date(zonedDateTimeLocalToISOString(formatLocalDate(nextDay, "00:00"), timezone)).getTime() - 1);

  return { start, end };
}

export function formatTimeOnly(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "09:00";
  }
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatLocalDate(parts: { year: number; month: number; day: number }, time: string): string {
  const year = String(parts.year).padStart(4, "0");
  const month = String(parts.month).padStart(2, "0");
  const day = String(parts.day).padStart(2, "0");

  return `${year}-${month}-${day}T${time}`;
}

function zonedDateTimeLocalToISOString(value: string, timezone: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);

  if (!match) {
    return "";
  }

  const [, rawYear, rawMonth, rawDay, rawHour, rawMinute] = match;
  const wallTimeAsUTC = Date.UTC(
    Number(rawYear),
    Number(rawMonth) - 1,
    Number(rawDay),
    Number(rawHour),
    Number(rawMinute),
    0,
    0
  );
  const firstOffset = getTimeZoneOffsetMs(new Date(wallTimeAsUTC), timezone);
  const firstGuess = wallTimeAsUTC - firstOffset;
  const secondOffset = getTimeZoneOffsetMs(new Date(firstGuess), timezone);

  return new Date(wallTimeAsUTC - secondOffset).toISOString();
}

function getTimeZoneOffsetMs(date: Date, timezone: string): number {
  const parts = getTimeZoneParts(date, timezone);
  const zonedAsUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);

  return zonedAsUTC - date.getTime();
}

function getTimeZoneParts(date: Date, timezone: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  });
  const entries = formatter.formatToParts(date).map((part) => [part.type, part.value]);
  const parts = Object.fromEntries(entries);

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second)
  };
}

function getUTCDateParts(date: Date): { year: number; month: number; day: number } {
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate()
  };
}
